import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { CheckSquare, Phone, CalendarDays, Flame, FileText, FileSignature, Building2, Wallet, ChevronRight, Lock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const ICONE = { task: CheckSquare, chiamate: Phone, appuntamenti: CalendarDays, notizie: Flame, documenti: FileText, proposte: FileSignature, immobili: Building2, incassi: Wallet };
const ora = (iso) => { if (!iso) return "—"; if (iso.length <= 10) return "—"; try { return new Date(iso.length === 16 ? `${iso}:00` : iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const oggiIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

function Gruppo({ g }) {
  const Ico = ICONE[g.gruppo] || FileText;
  return (
    <div className="card-immo p-5" data-testid={`fg-gruppo-${g.gruppo}`}>
      <div className="flex items-center gap-2 mb-2"><Ico size={16} className="text-[#FC82ED]" /><h3 className="font-bold text-sm">{g.label}</h3>
        <span className="ml-auto mono text-sm font-bold" data-testid={`fg-count-${g.gruppo}`}>{g.n}</span></div>
      {g.n === 0 ? <div className="text-xs text-[var(--muted)]">Niente oggi.</div> : g.voci.map((v, i) => {
        const body = (
          <>
            <span className="mono text-xs text-[var(--muted)] w-11 shrink-0">{ora(v.ora)}</span>
            <span className="min-w-0 flex-1"><span className="font-medium">{v.titolo}</span>{v.dettaglio && <span className="block text-[11px] text-[var(--muted)] truncate">{v.dettaglio}</span>}</span>
            {v.link && <ChevronRight size={14} className="text-slate-400 shrink-0" />}
          </>
        );
        return v.link
          ? <Link key={i} to={v.link} data-testid={`fg-voce-${g.gruppo}-${i}`} className="flex items-start gap-2 py-2 border-t border-[var(--border)] text-sm hover:bg-[#FC82ED]/5 rounded px-1">{body}</Link>
          : <div key={i} data-testid={`fg-voce-${g.gruppo}-${i}`} className="flex items-start gap-2 py-2 border-t border-[var(--border)] text-sm px-1">{body}</div>;
      })}
    </div>
  );
}

const domani = () => { const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Rome" })); d.setDate(d.getDate() + 1); return d.toLocaleDateString("sv-SE"); };
const GRUPPI_NF = { chiamate: "Chiamate", task: "Task", documenti: "Documenti da verificare" };

function NonFatte({ onChange }) {
  const [d, setD] = useState(null);
  const [date, setDate] = useState({});
  const load = () => api.get("/fine-giornata/non-fatte").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const key = (r) => `${r.voce}-${r.id}-${r.doc || ""}`;
  const rimanda = async (righe) => {
    try {
      const { data } = await api.post("/fine-giornata/rimanda", { voci: righe.map((r) => ({ voce: r.voce, id: r.id, entita: r.entita, doc: r.doc, titolo: r.titolo, tipo: r.tipo, data: date[key(r)] || domani() })) });
      toast.success(`${data.rimandate} ${data.rimandate === 1 ? "voce rimandata" : "voci rimandate"}`); load(); onChange?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <div className="skeleton h-32 rounded-2xl mt-6" />;
  return (
    <div className="mt-8" data-testid="fg-non-fatte">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <h2 className="font-display text-lg font-extrabold flex-1">Non fatte oggi <span className="text-[var(--muted)] font-semibold" data-testid="fg-non-fatte-count">({d.totale})</span></h2>
        {d.totale > 0 && <button data-testid="fg-rimanda-tutto" onClick={() => rimanda(d.righe)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#111111] text-white text-sm font-semibold"><CalendarClock size={15} /> Rimanda tutto a domani</button>}
      </div>
      {d.totale === 0 ? <div className="card-immo p-6 text-sm text-[var(--muted)]">Nessuna voce rimasta aperta.</div> : Object.entries(GRUPPI_NF).map(([g, lab]) => {
        const righe = d.righe.filter((r) => r.voce === g);
        if (!righe.length) return null;
        return (
          <div key={g} className="card-immo p-4 mb-3" data-testid={`fg-nf-${g}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1">{lab} · {righe.length}</div>
            {righe.map((r) => (
              <div key={key(r)} className="flex items-center gap-2 py-2 border-t border-[var(--border)] flex-wrap" data-testid={`fg-nf-riga-${key(r)}`}>
                <Link to={r.link} className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{r.titolo}</div><div className="text-[11px] text-[var(--muted)] truncate">{r.tipo} · {r.info} · {r.agente}</div></Link>
                <input type="date" data-testid={`fg-nf-data-${key(r)}`} min={domani()} value={date[key(r)] || domani()} onChange={(e) => setDate({ ...date, [key(r)]: e.target.value })} className="px-2 py-1 rounded-lg border border-[var(--border)] text-xs" />
                <button data-testid={`fg-nf-rimanda-${key(r)}`} onClick={() => rimanda([r])} className="px-2.5 py-1 rounded-lg border border-[var(--border)] bg-white text-xs font-semibold hover:border-[#FC82ED]/50">Rimanda a…</button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function FineGiornata() {
  const { user, isDirezione, isSegreteria } = useAuth();
  const vedeTutti = isDirezione || isSegreteria;
  const [utenti, setUtenti] = useState([]);
  const [uid, setUid] = useState("");
  const [giorno, setGiorno] = useState(oggiIso());
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [chiusa, setChiusa] = useState(null);
  const mio = !uid && giorno === oggiIso();
  useEffect(() => { if (mio) api.get("/fine-giornata/chiusura").then((r) => setChiusa(r.data?.chiusa_at ? r.data : null)).catch(() => {}); }, [mio]);
  const chiudi = async () => {
    try { const { data } = await api.post("/fine-giornata/chiudi"); setChiusa(data); toast.success("Giornata chiusa: riepilogo salvato"); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (vedeTutti) api.get("/fine-giornata/utenti").then((r) => setUtenti(r.data)).catch(() => {}); }, [vedeTutti]);
  useEffect(() => {
    setD(null); setErr(null);
    api.get("/fine-giornata", { params: { user_id: uid || undefined, data: giorno } }).then((r) => setD(r.data)).catch((e) => setErr(apiError(e)));
  }, [uid, giorno]);
  return (
    <div className="fade-up pb-20" data-testid="fine-giornata-page">
      <SectionTitle sub="Tutto ciò che è stato svolto e concluso nella giornata, raggruppato per tipo.">Fine giornata</SectionTitle>
      <div className="flex items-center gap-3 flex-wrap mb-5">
        {vedeTutti && (
          <label className="text-xs text-[var(--muted)] flex items-center gap-2">Utente
            <select data-testid="fg-utente" value={uid} onChange={(e) => setUid(e.target.value)} className="px-3 py-2 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white text-[#111]">
              <option value="">{user?.name} (io)</option>
              {utenti.filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-[var(--muted)] flex items-center gap-2">Giorno
          <input data-testid="fg-data" type="date" value={giorno} max={oggiIso()} onChange={(e) => setGiorno(e.target.value || oggiIso())} className="px-3 py-2 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white text-[#111]" />
        </label>
        {d && <span className="text-sm font-semibold" data-testid="fg-totale">{d.utente.nome}: {d.totale} attività</span>}
      </div>
      {err ? <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">{err}</div> : !d ? <div className="skeleton h-64 rounded-2xl" /> : (
        <div className="grid md:grid-cols-2 gap-4">{d.gruppi.map((g) => <Gruppo key={g.gruppo} g={g} />)}</div>
      )}
      {mio && isSegreteria && <NonFatte />}
      {mio && (
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <button data-testid="fg-chiudi-giornata" onClick={chiudi} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FC82ED] text-black font-bold text-sm hover:brightness-95 transition-[filter]"><Lock size={16} /> Chiudi giornata</button>
          {chiusa && <span className="text-sm text-[var(--muted)]" data-testid="fg-chiusa">Giornata chiusa alle {new Date(chiusa.chiusa_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {chiusa.totale} attività{chiusa.non_fatte != null ? ` · ${chiusa.non_fatte} rimaste aperte` : ""}</span>}
        </div>
      )}
    </div>
  );
}
